import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getRandomContent, recordProgress, awardBonus, Content } from '../services/api';
import { speak } from '../services/audio';
import './Games.css';

interface Card {
  id: string;
  text: string;
  type: 'english' | 'polish';
  contentId: string;
  matched: boolean;
}

const MatchingGame: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const lessonId = searchParams.get('lessonId') || undefined;
  const customSetId = searchParams.get('customSetId') || undefined;

  const [cards, setCards] = useState<Card[]>([]);
  const [selected, setSelected] = useState<Card | null>(null);
  const [matchedCount, setMatchedCount] = useState(0);
  const [mistakes, setMistakes] = useState(0);
  const [completed, setCompleted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState<Content[]>([]);

  useEffect(() => {
    const loadItems = async () => {
      try {
        const data = await getRandomContent(6, lessonId, customSetId);
        setItems(data);

        const gameCards: Card[] = [];
        data.forEach(item => {
          gameCards.push({ id: `en-${item.id}`, text: item.english, type: 'english', contentId: item.id, matched: false });
          gameCards.push({ id: `pl-${item.id}`, text: item.polish, type: 'polish', contentId: item.id, matched: false });
        });

        setCards(gameCards.sort(() => Math.random() - 0.5));
      } catch (err) {
        console.error('Failed to load content:', err);
      } finally {
        setLoading(false);
      }
    };
    loadItems();
  }, [lessonId, customSetId]);

  const handleCardClick = async (card: Card) => {
    if (card.matched) return;
    
    if (card.type === 'english') {
      speak(card.text);
    }

    if (!selected) {
      setSelected(card);
      return;
    }

    if (selected.id === card.id) {
      setSelected(null);
      return;
    }

    if (selected.contentId === card.contentId && selected.type !== card.type) {
      // Match!
      setCards(prev => prev.map(c => 
        c.contentId === card.contentId ? { ...c, matched: true } : c
      ));
      await recordProgress(card.contentId, true);
      setMatchedCount(prev => prev + 1);
      setSelected(null);

      if (matchedCount + 1 === items.length) {
        handleComplete();
      }
    } else {
      // No match
      setMistakes(prev => prev + 1);
      await recordProgress(selected.contentId, false);
      setSelected(null);
    }
  };

  const handleComplete = async () => {
    if (mistakes === 0) {
      await awardBonus(25, 'perfect_matching');
    }
    setCompleted(true);
  };

  if (loading) return <div className="loading">Ładowanie...</div>;
  if (items.length < 4 || cards.length === 0) {
    return (
      <div className="game-error">
        <h2>Za mało słówek!</h2>
        <p>Potrzebujesz minimum 4 słówek do tej gry.</p>
        <button className="btn btn-primary" onClick={() => navigate('/games')}>Wróć</button>
      </div>
    );
  }

  if (completed) {
    const isPerfect = mistakes === 0;
    return (
      <div className="game-complete fade-in">
        <div className="complete-icon">{isPerfect ? '🏆' : '🎉'}</div>
        <h1>{isPerfect ? 'Perfekcyjnie!' : 'Świetnie!'}</h1>
        <p>Błędy: {mistakes}</p>
        {isPerfect && <div className="bonus-badge bounce">+25 bonus!</div>}
        <button className="btn btn-primary btn-large" onClick={() => window.location.reload()}>
          Zagraj ponownie
        </button>
      </div>
    );
  }

  return (
    <div className="game-container fade-in">
      <div className="game-header">
        <h2>🔗 Dopasowywanie</h2>
        <div className="game-progress">{matchedCount} / {items.length}</div>
      </div>

      <div className="game-score">❌ Błędy: {mistakes}</div>

      <p className="quiz-instruction">Połącz angielskie słowa z polskimi tłumaczeniami</p>

      <div className="matching-grid">
        {cards.map(card => (
          <button
            key={card.id}
            className={`matching-card ${card.matched ? 'matched' : ''} 
              ${selected?.id === card.id ? 'selected' : ''}
              ${card.type}`}
            onClick={() => handleCardClick(card)}
            disabled={card.matched}
          >
            {card.text}
          </button>
        ))}
      </div>
    </div>
  );
};

export default MatchingGame;

